import { PoolType, ReserveType, parseRateLimiter } from "@renec-foundation/relend-adapter"
import BigNumber from "bignumber.js"
import BN from "bn.js"
import { atom } from "jotai"
import { atomFamily, atomWithDefault } from "jotai/utils"
import { configAtom } from "./config"
import { metadataAtom } from "./metadata"
import { SelectedPoolLogoType } from "./shared"

export const poolsStateAtom = atom<"initial" | "loading" | "error" | "done">((get) =>
  Object.values(get(poolsAtom)).reduce((acc, p) => p.reserves.length + acc, 0) === 0 ? "loading" : "done",
)

export const poolsAtom = atomWithDefault<PoolType[]>((get) => {
  const config = get(configAtom)

  return config.map((pool) => ({
    name: pool.name,
    authorityAddress: pool.authorityAddress,
    address: pool.address,
    owner: pool.owner,
    reserves: [] as Array<ReserveType>,
  }))
})

export const poolsFamily = atomFamily((address: string) =>
  atom((get) => {
    return get(poolsWithMetaDataAtom)[address]
  }),
)

export const poolsWithMetaDataAtom = atom((get) => {
  const metadata = get(metadataAtom)
  const pools = get(poolsAtom)

  return Object.fromEntries(
    Object.values(pools).map((p) => [
      p.address,
      {
        ...p,
        // This pool reserves are Object JSON, so we need to map them to ReserveType
        totalSupplyUsd: p.reserves.reduce((acc, r) => BigNumber(r.totalSupplyUsd).plus(acc), BigNumber(0)),
        reserves: p.reserves.map((r) => ({
          ...r,
          accumulatedProtocolFeesWad: new BN(r.accumulatedProtocolFeesWad),
          totalBorrowWad: new BN(r.totalBorrowWad),
          availableAmountWad: new BN(r.availableAmountWad),
          mintTotalSupplyWad: new BN(r.mintTotalSupplyWad),
          disabled: Boolean(r.disabled),
          cumulativeBorrowRate: BigNumber(r.cumulativeBorrowRate),
          cTokenExchangeRate: BigNumber(r.cTokenExchangeRate),
          cTokenExchangeRateMintSupplyOnTotalSupply: BigNumber(r.cTokenExchangeRateMintSupplyOnTotalSupply),
          reserveUtilization: BigNumber(r.reserveUtilization),
          reserveSupplyLimit: BigNumber(r.reserveSupplyLimit),
          reserveBorrowLimit: BigNumber(r.reserveBorrowLimit),
          borrowFee: BigNumber(r.borrowFee),
          flashLoanFee: BigNumber(r.flashLoanFee),
          protocolLiquidationFee: Number(r.protocolLiquidationFee),
          hostFee: Number(r.hostFee),
          interestRateSpread: Number(r.interestRateSpread),
          reserveSupplyCap: BigNumber(r.reserveSupplyCap),
          reserveBorrowCap: BigNumber(r.reserveBorrowCap),
          targetBorrowApr: Number(r.targetBorrowApr),
          targetUtilization: Number(r.targetUtilization),
          maxUtilizationRate: Number(r.maxUtilizationRate),
          minBorrowApr: Number(r.minBorrowApr),
          maxBorrowApr: Number(r.maxBorrowApr),
          superMaxBorrowRate: Number(r.superMaxBorrowRate),
          supplyAPRInterest: BigNumber(r.supplyAPRInterest),
          borrowAPRInterest: BigNumber(r.borrowAPRInterest),
          supplyAPYInterest: BigNumber(r.supplyAPYInterest),
          borrowAPYInterest: BigNumber(r.borrowAPYInterest),
          totalSupply: BigNumber(r.totalSupply),
          totalBorrow: BigNumber(r.totalBorrow),
          availableAmount: BigNumber(r.availableAmount),
          rateLimiter: {
            previousQuantity: BigNumber(r.rateLimiter.previousQuantity),
            windowStart: BigNumber(r.rateLimiter.windowStart),
            remainingOutflow:
              String(r.rateLimiter.remainingOutflow) !== "null" ? BigNumber(r.rateLimiter.remainingOutflow!) : null,
            config: {
              windowDuration: BigNumber(r.rateLimiter.config.windowDuration),
              maxOutflow: BigNumber(r.rateLimiter.config.maxOutflow),
            },
            currentQuantity: BigNumber(r.rateLimiter.currentQuantity),
          },
          totalSupplyUsd: BigNumber(r.totalSupplyUsd),
          totalBorrowUsd: BigNumber(r.totalBorrowUsd),
          availableAmountUsd: BigNumber(r.availableAmountUsd),
          loanToValueRatio: Number(r.loanToValueRatio),
          liquidationThreshold: Number(r.liquidationThreshold),
          maxLiquidationThreshold: Number(r.maxLiquidationThreshold),
          liquidationPenalty: Number(r.liquidationPenalty),
          maxLiquidationPenalty: Number(r.maxLiquidationPenalty),
          decimals: Number(r.decimals),
          price: BigNumber(r.price),
          addedBorrowWeightBPS: new BN(r.addedBorrowWeightBPS),
          borrowWeight: Number(r.borrowWeight),
          lastUpdateSlot: new BN(r.lastUpdateSlot),
          emaPrice: r.emaPrice ? Number(r.emaPrice) : undefined,
          minPrice: BigNumber(r.minPrice),
          maxPrice: BigNumber(r.maxPrice),
          name: metadata[r.mintAddress]?.name,
          symbol: metadata[r.mintAddress]?.symbol,
          logo: metadata[r.mintAddress]?.logoUri as SelectedPoolLogoType,
        })),
      } as PoolType,
    ]),
  )
})

const CURRENT_SLOT_DEFAULT = 174_341_956

export const currentSlotAtom = atom<number>(CURRENT_SLOT_DEFAULT)

export const rateLimiterAtom = atom<Awaited<ReturnType<typeof parseRateLimiter>> | null>(null)

export const selectedMainPoolAddressAtom = atomWithDefault<string | null>((get) => {
  const config = get(configAtom)

  return config[0]?.address
})

export const selectedReserveAddressAtom = atom<string | null>(null)

export const selectedMainPoolAtom = atom((get) => {
  const selectedPoolAddress = get(selectedMainPoolAddressAtom)
  if (!selectedPoolAddress) return null
  const metadata = get(metadataAtom)
  const selectedPool = get(poolsFamily(selectedPoolAddress))
  return {
    ...selectedPool,
    reserves: selectedPool
      ? selectedPool.reserves.map((r) => {
          const addressString = r.mintAddress
          const tokenMetadata = metadata[addressString]

          return {
            ...r,
            name: tokenMetadata?.name,
            symbol: tokenMetadata?.symbol,
            logo: tokenMetadata?.logoUri as SelectedPoolLogoType,
          }
        })
      : [],
  }
})
